import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Eye, EyeOff, X, ArrowLeft, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TranslatedText } from '@/components/TranslatedText';
import { LanguageSelector } from '@/components/LanguageSelector';

const loginSchema = z.object({
  email: z.string().trim().email("Invalid email address").max(255),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginData = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();
  const { signIn, user, role, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LoginData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      if (role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof LoginData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof LoginData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setIsLoading(true);

    const { error } = await signIn(formData.email.toLowerCase().trim(), formData.password);
    
    if (error) {
      setIsLoading(false);
      if (error.message.includes('Invalid login credentials')) {
        setErrors({ email: 'Invalid email or password' });
      } else {
        toast.error(error.message);
      }
      return;
    }

    setIsLoading(false);
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    const emailSchema = z.string().email();
    const result = emailSchema.safeParse(resetEmail.trim());
    if (!result.success) {
      setResetError('Please enter a valid email address');
      return;
    }

    setResetLoading(true);
    setResetError('');

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.toLowerCase().trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setResetLoading(false);

    if (error) {
      setResetError(error.message);
      return;
    }

    toast.success('Password reset email sent. Check your inbox.');
    setShowForgotPassword(false);
    setResetEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Language selector - top right corner */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector variant="ghost" className="text-slate-600 hover:text-slate-900" />
      </div>
      
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              <TranslatedText>Welcome Back</TranslatedText>
            </h1>
            <p className="text-slate-600 text-sm sm:text-base">
              <TranslatedText>Sign in to access your exclusive content</TranslatedText>
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 text-sm font-medium">
                  <TranslatedText>Email</TranslatedText>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-12 text-base rounded-xl"
                  placeholder="your@email.com"
                />
                {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                  <TranslatedText>Password</TranslatedText>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 pr-12 h-12 text-base rounded-xl"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2 -m-2"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(formData.email);
                    setShowForgotPassword(true);
                  }}
                  className="text-blue-600 text-sm hover:text-blue-700 transition-colors mt-1"
                >
                  <TranslatedText>Forgot password?</TranslatedText>
                </button>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/25 transition-all active:scale-[0.98]"
                disabled={isLoading}
              >
                {isLoading ? <TranslatedText>Signing in...</TranslatedText> : <TranslatedText>Sign In</TranslatedText>}
              </Button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center gap-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-slate-400 text-sm"><TranslatedText>or</TranslatedText></span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Sign Up Link */}
            <Link to="/apply">
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full h-14 text-base border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 font-semibold rounded-xl active:scale-[0.98]"
              >
                <TranslatedText>Don't have an account? Sign up</TranslatedText>
              </Button>
            </Link>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <TranslatedText>Back to Home</TranslatedText>
            </Link>
          </div>

          {/* Trust indicator */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4 text-green-500" />
            <span><TranslatedText>Secure login protected by encryption</TranslatedText></span>
          </div>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
              onClick={() => setShowForgotPassword(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8"
            >
              <button
                onClick={() => setShowForgotPassword(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={20} />
              </button>

              <h2 className="text-xl font-bold text-slate-900 mb-2">
                <TranslatedText>Reset Password</TranslatedText>
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                <TranslatedText>Enter your email and we'll send you a password reset link.</TranslatedText>
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-slate-700 text-sm font-medium">
                    <TranslatedText>Email Address</TranslatedText>
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      setResetError('');
                    }}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 placeholder:text-slate-400 h-12 rounded-xl"
                    placeholder="your@email.com"
                  />
                  {resetError && <p className="text-red-500 text-xs">{resetError}</p>}
                </div>

                <Button
                  onClick={handlePasswordReset}
                  size="lg"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
                  disabled={resetLoading}
                >
                  {resetLoading ? <TranslatedText>Sending...</TranslatedText> : <TranslatedText>Send Reset Link</TranslatedText>}
                </Button>

                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-slate-500 text-sm hover:text-slate-700 transition-colors py-2"
                >
                  <TranslatedText>Cancel</TranslatedText>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;
